/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";

import { useFormik } from "formik";

import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";

import { useApi } from "@/hooks/useApi";
import { useAttendanceId } from "@/hooks/useAttendanceId";
import { useSwal } from "@/hooks/useSwal";
import { preSurgery as preSurgeryInitialValues } from "@/utils/initialValues/preSurgery";
import { filterObject, filterObjectExcluding } from "@/utils/filterObject";
import { ConformityType, ConformityTypeMessages } from "@/enums/ConformityType";

import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { FormRadio } from "@/components/Radio/FormRadio";
import { SaveButton } from "@/components/Buttons/SaveButton";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { ProfessionalWithRolesSelect } from "@/components/Selects/ProfessionalWithRolesSelect";

import "./styles.scss";

type Props = {
	surgicalAttendance: SurgicalAttendance;
	remainingTime: number;
};

type PreSurgery = typeof preSurgeryInitialValues;

export function PreSurgeryForm({ surgicalAttendance, remainingTime }: Props) {
	const firstRender = useRef(true);
	const alreadyStored = useRef(false);

	const [skeleton, setSkeleton] = useState(true);

	const { api } = useApi();
	const { attendanceId } = useAttendanceId();
	const { Toast } = useSwal();

	const expertise = surgicalAttendance?.expertise;
	const isPregnant = expertise?.pregnant;

	async function fetchPreSurgery() {
		try {
			const { data } = await api.get(`/surgical-center/${attendanceId}/pre-surgery`);

			const preSurgeryInitialValuesKeys = Object.keys(preSurgeryInitialValues);
			const filteredPreSurgery = filterObject(data, preSurgeryInitialValuesKeys);

			formik.setValues({
				...formik.values,
				...filteredPreSurgery,
			});

			alreadyStored.current = true;
		} finally {
			setSkeleton(false);
		}
	}

	async function sendData(values: typeof preSurgeryInitialValues) {
		const method = alreadyStored.current ? "put" : "post";

		const filteredValues = filterObjectExcluding(formik.values, [
			"cribs_and_heated_fields",
		]) as PreSurgery;

		const { data } = await api[method](
			`/surgical-center/${attendanceId}/pre-surgery`,
			isPregnant ? values : filteredValues
		);

		return data;
	}

	const formik = useFormik({
		initialValues: preSurgeryInitialValues,
		async onSubmit(values) {
			try {
				sendData(values);

				Toast.fire({
					text: "Dados da Pré Cirurgia salvos com sucesso!",
					icon: "success",
				});

				alreadyStored.current = true;
			} catch (error) {
				Toast.fire({
					title: "Erro ao salvar admissão de enfermagem!",
					icon: "error",
				});
			}
		},
	});

	async function handleSubmit() {
		try {
			sendData(formik.values);

			alreadyStored.current = true;
		} catch (error) {
			Toast.fire({
				title: "Erro ao salvar Pré Cirurgia!",
				icon: "error",
			});
		}
	}

	useEffect(() => {
		fetchPreSurgery();
	}, []);

	useEffect(() => {
		if (!alreadyStored.current && !skeleton) {
			handleSubmit();
		}
	}, [skeleton]);

	useEffect(() => {
		if (!firstRender.current && alreadyStored.current && !skeleton && remainingTime === 0) {
			handleSubmit();
		} else {
			firstRender.current = false;
		}
	}, [remainingTime]);

	return (
		<Form className="px-5 pb-7" onSubmit={formik.handleSubmit}>
			<div className="border-secondary border-bottom">
				<TagHTMLOrSkeleton
					text="Centro Cirúrgico"
					tag="h6"
					className="font-weight-bold mt-3 mb-6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<div className="d-flex flex-column">
					<Col
						xl={12}
						className="d-flex flex-responsive pb-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="confirmed_team"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="Todos os profissionais da equipe confirmam seus nomes e profissões?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.confirmed_team}
						>
							<FormRadio
								id="confirm-team-compliant"
								containerClassName="width-6.5rem"
								label="Sim"
								value="1"
							/>

							<FormRadio id="confirm-team-not-compliant" label="Não" value="0" />
						</FormGroupRadio>
					</Col>

					<Col
						xl={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="confirmed_patient_data"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="O cirurgião ou obstetra, o anestesista e a enfermagem confirmam verbalmente a identificação do paciente, local da cirurgia e procedimento a ser realizado?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.confirmed_patient_data}
						>
							<FormRadio
								id="confirmed-patient-data-compliant"
								containerClassName="width-6.5rem"
								label="Sim"
								value="1"
							/>

							<FormRadio
								label="Não"
								value="0"
								id="confirmed-patient-data-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xl={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="resource_structuring_present"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="Estruturação de recursos presente (incluindo kit e intubação difícil/prazo de esterilização)?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.resource_structuring_present}
						>
							<FormRadio
								id="resource-structuring-present-compliant"
								containerClassName="width-6.5rem"
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
							/>

							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="resource-structuring-present-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xl={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="requested_opsm_present"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="OPME solicitado se encontra na sala de cirurgia?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.requested_opsm_present}
						>
							<FormRadio
								id="requested-opsm-present-compliant"
								containerClassName="width-6.5rem"
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
							/>

							<FormRadio
								id="requested-opsm-present-not-compliant"
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
							/>
						</FormGroupRadio>
					</Col>

					<Col xl={12} className="d-flex flex-responsive py-5">
						<FormGroupRadio
							inline
							name="prophylactic_antimicrobial_administered"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="O antimicrobiano profilático foi administrado nos últimos 60 minutos?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.prophylactic_antimicrobial_administered}
						>
							<FormRadio
								id="prophylactic-antimicrobial-administered-compliant"
								containerClassName="width-6.5rem"
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
							/>

							<FormRadio
								id="prophylactic-antimicrobial-administered-not-compliant"
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
							/>

							<FormRadio
								id="prophylactic-antimicrobial-administered-not-applicable"
								label={ConformityTypeMessages[ConformityType.NotApplicable]}
								value={ConformityType.NotApplicable}
							/>
						</FormGroupRadio>
					</Col>
				</div>
			</div>

			<div className="py-5 border-secondary border-bottom">
				<TagHTMLOrSkeleton
					text="Antecipação de eventos críticos - Revisão do cirurgião"
					tag="h5"
					skeletonClassName="mb-4"
					skeleton={skeleton}
				/>

				<div className="d-flex flex-column gap-4">
					{skeleton ? (
						<SkeletonWrapper height={20} />
					) : (
						<Form.Group className="pre-surgery-input-container w-100 d-flex flex-responsive align-items-center gap-2">
							<Form.Label className="width-15rem">
								Há passos críticos na cirurgia?
							</Form.Label>

							<div className="col-xl-4">
								<Form.Control
									className="py-2"
									name="critical_steps_in_surgery"
									maxLength={100}
									onChange={formik.handleChange}
									value={formik.values.critical_steps_in_surgery}
								/>
							</div>
						</Form.Group>
					)}

					{skeleton ? (
						<SkeletonWrapper height={20} />
					) : (
						<Form.Group className="pre-surgery-input-container w-100 d-flex flex-responsive align-items-center gap-2">
							<Form.Label className="width-15rem">Duração estimada?</Form.Label>

							<div className="col-xl-4">
								<Form.Control
									className="py-2"
									name="estimated_duration"
									maxLength={40}
									onChange={formik.handleChange}
									value={formik.values.estimated_duration}
								/>
							</div>
						</Form.Group>
					)}
				</div>
			</div>

			<div className="py-5 border-secondary border-bottom">
				<TagHTMLOrSkeleton
					text="Antecipação de eventos críticos - Revisão do anestesista"
					tag="h5"
					skeletonClassName="mb-4"
					skeleton={skeleton}
				/>

				<Col xl={12} className="d-flex flex-responsive">
					<FormGroupRadio
						inline
						name="specific_concern_patient"
						containerClassName="flex-grow-1"
						labelClassName="col-xl-6 me-8"
						label="Há alguma preocupação especifica em relação ao paciente?"
						skeleton={skeleton}
						skeletonHeight={20}
						onChange={formik.handleChange}
						requestedValue={formik.values.specific_concern_patient}
					>
						<FormRadio
							id="specific-concern-patient-compliant"
							containerClassName="width-6.5rem"
							value="1"
							label="Sim"
						/>

						<FormRadio
							id="specific-concern-patient-not-compliant"
							label="Não"
							value="0"
						/>
					</FormGroupRadio>
				</Col>
			</div>

			<div className="py-5">
				<TagHTMLOrSkeleton
					text="Antecipação de eventos críticos - Revisão da enfermagem"
					tag="h5"
					skeletonClassName="mb-4"
					skeleton={skeleton}
				/>

				<div className="d-flex flex-column">
					<Col xl={12} className="d-flex flex-responsive pb-5">
						<FormGroupRadio
							inline
							name="correct_sterilization_on_expiration_date"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="Houve correta esterilização do instrumental cirúrgico estão dentro da validade?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.correct_sterilization_on_expiration_date}
						>
							<FormRadio
								id="correct-sterilization-on-expiration-date-compliant"
								containerClassName="width-6.5rem"
								label="Sim"
								value="1"
							/>

							<FormRadio
								id="correct-sterilization-on-expiration-date-not-compliant"
								label="Não"
								value="0"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xl={12}
						className="d-flex flex-responsive pb-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="concern_about_equipment_materials"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="Há alguma preocupação em relação aos equipamentos/materiais?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.concern_about_equipment_materials}
						>
							<FormRadio
								id="concern-about-equipment-materials-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio
								id="concern-about-equipment-materials-not-compliant"
								value="0"
								label="Não"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xl={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="needs_exams_xr_freezing"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="Há necessidades de exames, RX, congelação?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.needs_exams_xr_freezing}
						>
							<FormRadio
								id="needs-exams-xr-freezing-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio
								id="needs-exams-xr-freezing-not-compliant"
								label="Não"
								value="0"
							/>
						</FormGroupRadio>
					</Col>
				</div>
			</div>

			{isPregnant && (
				<div className="pb-5 border-secondary border-bottom">
					<TagHTMLOrSkeleton
						text="Obstetrícia"
						tag="h5"
						skeletonClassName="mb-4"
						skeleton={skeleton}
					/>

					<Col xl={12} className="d-flex flex-responsive">
						<FormGroupRadio
							inline
							name="cribs_and_heated_fields"
							containerClassName="flex-grow-1"
							labelClassName="col-xl-6 me-8"
							label="Berços e campos aquecidos?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.cribs_and_heated_fields}
						>
							<FormRadio
								id="cribs-and-heated-fields-compliant"
								value="1"
								label="Sim"
								containerClassName="width-6.5rem"
							/>

							<FormRadio
								id="cribs-and-heated-fields-not-compliant"
								label="Não"
								value="0"
							/>
						</FormGroupRadio>
					</Col>
				</div>
			)}

			<Col xl={12} className="py-5">
				{skeleton ? (
					<SkeletonWrapper height={20} />
				) : (
					<div className="professional-select-container d-flex flex-responsive align-items-center gap-2">
						<TagHTMLOrSkeleton
							className="width-15rem mb-0"
							text="Profissional responsável:"
							tag="h5"
						/>

						<ProfessionalWithRolesSelect
							classNames={{
								container: () => "col-xl-4 col-md-4",
							}}
							menuPlacement="auto"
							roles={["Técnico de Enfermagem", "Enfermeiro"]}
							isSolid
							professionalId={formik.values.responsible_professional_id}
							onChange={(option) =>
								formik.setFieldValue(
									"responsible_professional_id",
									String((option as ReactSelectOption).value)
								)
							}
						/>
					</div>
				)}
			</Col>

			<div className="mt-20 d-flex justify-content-end">
				<SaveButton type="submit" skeleton={skeleton} />
			</div>
		</Form>
	);
}
